import { useTranslation } from "react-i18next";
import { AttemptHistoryGrid, GenderLabLoader } from "../../../components";
import {
  Text,
  Stack,
  Button,
  Box,
  Flex,
  Title,
  Alert,
  Anchor,
} from "@mantine/core";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import {
  TQuiz,
  TAnswer,
  TForm,
  TAttempt,
} from "../../../types/Aula";
import { UseFormReturnType } from "@mantine/form";
import { aulaService } from "../../../api";
import { IconInfoCircle } from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import { renderNode } from "../../../utils/renderNode";
import { useMediaQuery } from "@mantine/hooks";
import { linkColor } from "../../../utils";
import { QuizTabs } from "../QuizTabs";
import { isAnswered } from "../../../utils/isAnswered";

export const Quiz = ({
  quiz,
  enrollmentId,
  form,
  attemptId,
  attempts,
  setAttemptId,
  handleSubmit,
  lessonId,
}: {
  quiz: TQuiz | null;
  enrollmentId: string | undefined;
  form: UseFormReturnType<TForm>;
  attemptId?: string | null;
  attempts: TAttempt[];
  setAttemptId?: React.Dispatch<React.SetStateAction<string | null>>;
  handleSubmit: (values: TAnswer[], attemptId: string) => void;
  lessonId?: string;
}) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [showIntermediateScreen, setShowIntermediateScreen] = useState<boolean>(
    !attemptId
  );
  const isMobile = useMediaQuery("(max-width: 992px)");
  // for intermediate page
  const [lessonContent, setLessonContent] = useState<any>(null);

  const getContentByLessonId = useCallback(
    async (lessonId: string): Promise<boolean | null> => {
      try {
        const response = await aulaService.getContent(lessonId);
        setLessonContent(response.data);
        return true;
      } catch (error: any) {
        if (error.response?.status !== 404) {
          showNotification({
            title: "Error",
            message: error.response?.data?.error || "Ocurrió un error",
            color: "red",
          });
          console.error(error);
          return null;
        }
        return false;
      }
    },
    []
  );

  useLayoutEffect(() => {
    const fetchContent = async () => {
      try {
        if (lessonId) {
          await getContentByLessonId(lessonId);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  useEffect(() => {
    if (quiz) {
      if (quiz.time_limit) setTimeLeft(quiz.time_limit * 60);
    }
  }, [quiz]);

  useEffect(() => {
    if (attemptId) {
      setShowIntermediateScreen(false);
    }
  }, [attemptId]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (timeLeft !== null && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime === 1) {
            handleSubmit(parsedValues(form.values), attemptId!);
            clearInterval(timer);
            return 0;
          }
          return prevTime! - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const formatTime = (timeInSeconds: number) => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = timeInSeconds % 60;
    let formattedTime = "";
    if (hours > 0) {
      formattedTime += hours + ":";
    }
    formattedTime += (minutes < 10 ? "0" : "") + minutes + ":";
    formattedTime += (seconds < 10 ? "0" : "") + seconds;
    return formattedTime;
  };

  const startQuiz = async () => {
    try {
      const data = { enrollment_id: enrollmentId };
      const attempt = await aulaService.startAttempt(quiz!.id, data);
      setAttemptId?.(attempt.data.id);
      setShowIntermediateScreen(false);
    } catch (error) {
      console.error("Error starting the quiz attempt:", error);
    }
  };

  const parsedValues = (values: TForm): TAnswer[] => {
    return Object.entries(values)
      .filter(([questionId]) => isAnswered(form, questionId))
      .map(([question, option_choice]) => ({
        question,
        option_choice,
      })) as TAnswer[];
  };

  if (
    attemptId === null ||
    enrollmentId === undefined
  ) {
    return <GenderLabLoader />;
  }

  if (
    lessonContent !== null &&
    !showIntermediateScreen
  ) {
    return (
      <>
        <Title order={1}>{lessonContent?.name}</Title>
        {renderNode(lessonContent?.content)}
        {!lessonContent?.hasErrors && (
          <Flex justify="center" mt="md">
            <Button
              onClick={() => setLessonContent(null)}
              size={isMobile ? "sm" : "md"}
            >
              Ir al quiz
            </Button>
          </Flex>
        )}
      </>
    );
  }

  if (
    attempts.length > 0 &&
    attempts[0].is_approved === true
  ) {
    return (
      <Flex ta="center" justify="center">
        <Alert
          variant="light"
          mt="md"
          w="50%"
          color="blue"
          title="Felicitaciones 🏆"
          ta="justify"
          icon={<IconInfoCircle />}
        >
          Has completado el quiz con éxito.
          <br />
          ¡Gracias por tu esfuerzo y dedicación!
        </Alert>
      </Flex>
    );
  }

  if (
    quiz &&
    quiz.max_attempts !== null &&
    attempts.length >= quiz.max_attempts
  ) {
    return (
      <Flex ta="center" justify="center">
        <Alert
          variant="light"
          mt="md"
          w="50%"
          color="red"
          title="Límite de intentos 😔"
          ta="justify"
          icon={<IconInfoCircle />}
        >
          Has alcanzado el límite de intentos permitidos para esta evaluación.
          <br />
          Puedes ponerte en contacto con{" "}
          <Text component="span" size="sm">
            <Anchor
              href="mailto:support@elsa.la"
              style={{
                color: linkColor,
                textDecoration: "underline",
              }}
            >
              support@elsa.la
            </Anchor>{" "}
            si necesitas ayuda.
          </Text>
        </Alert>
      </Flex>
    );
  }

  return (
    <>
      {quiz && (
        <>
          {showIntermediateScreen && (
            <Box>
              <Stack gap="xs">
                <Text size="lg" fw={700} ta="center">
                  {quiz.name}
                </Text>
                <Text size="sm">
                  <Text component="span" fw={500}>
                    {t("quiz.questions")}
                  </Text>{" "}
                  {quiz.questions.length}
                </Text>
                <Text size="sm">
                  <Text component="span" fw={500}>
                    {t("quiz.minimumPercentageToPass")}
                  </Text>{" "}
                  {quiz.minimum_percentage_to_pass * 100}%
                </Text>
                {quiz.time_limit !== null ? (
                  <Text size="sm">
                    <Text component="span" fw={500}>
                      {t("quiz.timeLimit")}
                    </Text>{" "}
                    {formatTime(quiz.time_limit)}
                  </Text>
                ) : (
                  <Text size="sm" fw={500}>
                    {t("quiz.noTimeLimit")}
                  </Text>
                )}
                {quiz.max_attempts !== null ? (
                  <Text size="sm">
                    <Text component="span" fw={500}>
                      {t("quiz.maxAttempts")}
                    </Text>{" "}
                    {quiz.max_attempts}
                  </Text>
                ) : (
                  <Text size="sm" fw={500}>
                    {t("quiz.unlimitedAttempts")}
                  </Text>
                )}
                <AttemptHistoryGrid attempts={attempts} />
              </Stack>
              <Flex justify="center" mt="lg" gap="sm">
                <Button onClick={startQuiz}>{t("actions.start")}</Button>
              </Flex>
            </Box>
          )}
          {!showIntermediateScreen && (
            <Box>
              <Title order={3} ta="center">
                {quiz.name}
              </Title>
              {timeLeft !== null && (
                <Text size="lg" c="red" ta="right">
                  {formatTime(timeLeft)}
                </Text>
              )}
              <form
                onSubmit={form.onSubmit((values) => {
                  handleSubmit(parsedValues(values), attemptId!);
                })}
              >
                <QuizTabs
                  questions={quiz.questions}
                  form={form}
                />
              </form>
            </Box>
          )}
        </>
      )}
    </>
  );
};
